import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Heart } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { myWishlistQuery, removeFromWishlist } from "@/lib/wishlist.functions";
import { formatBRL } from "@/lib/format";

export const Route = createFileRoute("/minha-conta/favoritos")({
  head: () => ({
    meta: [{ title: "Meus Favoritos — Loja EG Elétrica" }, { name: "robots", content: "noindex" }],
  }),
  component: FavoritosPage,
});

function FavoritosPage() {
  const user = useAuth((s) => s.user);
  const initialized = useAuth((s) => s.initialized);
  const queryClient = useQueryClient();

  const { data: products, isLoading } = useQuery({ ...myWishlistQuery(), enabled: !!user });

  const [removingId, setRemovingId] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState("");

  const handleRemove = async (productId: string) => {
    setRemovingId(productId);
    setRemoveError("");
    try {
      await removeFromWishlist({ data: productId });
      queryClient.invalidateQueries({ queryKey: ["my-wishlist"] });
    } catch {
      setRemoveError("Não foi possível remover este favorito. Tente novamente.");
    } finally {
      setRemovingId(null);
    }
  };

  if (initialized && !user) {
    return (
      <div className="mx-auto max-w-sm px-4 py-12 text-center">
        <h1 className="font-display text-2xl font-bold text-eg-dark mb-3">Meus Favoritos</h1>
        <p className="mb-4 text-sm text-muted-foreground">
          Entre na sua conta para ver seus favoritos.
        </p>
        <Link to="/login" className="text-sm font-medium text-eg-goldtext hover:underline">
          Entrar
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="font-display text-2xl font-bold text-eg-dark mb-6">Meus Favoritos</h1>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-eg-gold" />
        </div>
      )}

      {!isLoading && products?.length === 0 && (
        <div className="py-12 text-center text-muted-foreground">
          <Heart className="mx-auto mb-3 h-10 w-10" />
          Você ainda não favoritou nenhum produto.
        </div>
      )}

      {removeError && <p className="mb-3 text-xs text-red-600">{removeError}</p>}

      <div className="space-y-3">
        {products?.map((product) => (
          <div
            key={product.id}
            className="flex items-center gap-4 rounded-lg border border-border bg-white p-4"
          >
            {product.images[0] && (
              <img
                src={product.images[0]}
                alt={product.name}
                loading="lazy"
                className="h-16 w-16 rounded object-cover"
              />
            )}
            <div className="flex-1">
              <Link
                to="/produto/$slug"
                params={{ slug: product.slug }}
                className="font-semibold text-eg-dark hover:text-eg-goldtext"
              >
                {product.name}
              </Link>
              <p className="text-sm text-eg-goldtext font-semibold">{formatBRL(product.price)}</p>
            </div>
            <button
              type="button"
              onClick={() => handleRemove(product.id)}
              disabled={removingId === product.id}
              className="flex items-center gap-1 text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
            >
              {removingId === product.id && <Loader2 className="h-3 w-3 animate-spin" />}
              Remover
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
